import { useAuthStore } from '@/lib/stores/authStore'
import axios from 'axios'
import Cookies from 'js-cookie'
import { MENUS } from '@/containers/SideNavigationBar/_const/menu'

const baseURL = process.env.NEXT_PUBLIC_API_URL || ''

export const api = axios.create({ baseURL })

// 동시성 제어: refresh 요청은 한 번만 날리고, 나머지는 해당 Promise를 기다리게 함
let refreshRequest: Promise<{ access: string; refresh: string }> | null = null

async function getOrRequestTokenRefresh() {
  if (!refreshRequest) {
    const currentRefreshToken = Cookies.get('axp_refresh_token')
    refreshRequest = axios
      .post(`${baseURL}/admin/v1/auth/admins/token`, {
        refresh_token: currentRefreshToken,
      })
      .then(({ data }) => {
        const newAccess = data.data.access_token
        const newRefresh = data.data.refresh_token

        Cookies.set('axp_access_token', newAccess)
        Cookies.set('axp_refresh_token', newRefresh)

        useAuthStore.getState().setTokens(newAccess, newRefresh)
        // 향후 요청 기본 Authorization 헤더도 최신화(선제적으로 세팅)
        api.defaults.headers.common.Authorization = `Bearer ${newAccess}`

        return { access: newAccess, refresh: newRefresh }
      })
      .finally(() => {
        refreshRequest = null
      })
  }
  return refreshRequest
}

// 현재 경로와 MENUS를 기준으로 가장 잘 매칭되는 메뉴 코드 찾기
function getCurrentMenuCode(pathname: string): string | null {
  try {
    const entries = Object.values(MENUS).filter(
      (m: any) => typeof (m as any).href === 'string' && (m as any).href,
    ) as Array<{ id: string; href: string }>

    // 정적/동적 경로 모두 지원: {param} 자리는 1세그먼트 와일드카드로 처리하고 하위 경로 허용
    const hrefToRegex = (href: string) => {
      const escaped = href.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
      const withParams = escaped.replace(/\\{[^/]+\\}/g, '[^/]+')
      return new RegExp(`^${withParams}(?:/.*)?$`)
    }

    let bestMatch: { id: string; href: string } | null = null

    for (const m of entries) {
      if (!m.href) continue
      const re = hrefToRegex(m.href)
      if (re.test(pathname)) {
        if (!bestMatch || m.href.length > bestMatch.href.length) {
          bestMatch = m
        }
      }
    }

    return bestMatch ? bestMatch.id : null
  } catch {
    return null
  }
}

// 토큰 추가 인터셉터
api.interceptors.request.use(
  config => {
    // 메뉴 코드 헤더 추가 (클라이언트에서만)
    if (typeof window !== 'undefined') {
      const pathname = window.location?.pathname || '/'
      const menuCode = getCurrentMenuCode(pathname)
      if (menuCode) {
        config.headers = config.headers || {}
        ;(config.headers as any)['X-Menu-Code'] = menuCode
      }
    }

    const token = Cookies.get('axp_access_token')
    if (token) config.headers.Authorization = `Bearer ${token}`
    return config
  },
  error => Promise.reject(error),
)

// 응답 인터셉터 - 토큰 만료 시 재발급 시도
api.interceptors.response.use(
  res => res,
  async error => {
    const originalRequest = error.config
    console.log('error', error)

    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true
      try {
        const { access } = await getOrRequestTokenRefresh()
        originalRequest.headers.Authorization = `Bearer ${access}`
        return api(originalRequest)
      } catch (err) {
        useAuthStore.getState().clearTokens()
        window.location.href = '/login'
      }
    }

    return Promise.reject(error)
  },
)
